import { Box, Card, CardContent, CssBaseline, Stack, ThemeProvider } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import type { CognitoUser } from "../../../lib/cognito/src/lib/amplifyAuth";
import { getCognitoSingleton } from "../../../lib/cognito/src/lib/cognitoSingleton";
import { EmailAuthTest } from "./components/EmailAuthTest";
import { PhoneAuthTest } from "./components/PhoneAuthTest";
import { SessionApiTest } from "./components/SessionApiTest";

export function CognitoTestPage() {
  const history = useHistory();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pageWrapperRef = useRef<HTMLDivElement>(null);
  const [result, setResult] = useState("");
  const [apiResult, setApiResult] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<CognitoUser | undefined>(undefined);

  const cognitoAuth = getCognitoSingleton();

  useEffect(() => {
    const checkCurrentUser = async () => {
      try {
        const user = await cognitoAuth.getCurrentUserInfo();
        setCurrentUser(user);
        if (user) {
          setResult(`Current user:\n${JSON.stringify(user, null, 2)}`);
        }
      } catch {
        setCurrentUser(undefined);
      }
    };

    void checkCurrentUser();
  }, [cognitoAuth]);

  // Scroll to bottom when result or apiResult changes
  useEffect(() => {
    if (result || apiResult) {
      setTimeout(() => {
        pageWrapperRef.current?.scrollTo({
          top: pageWrapperRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [result, apiResult]);

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <PageWrapper ref={pageWrapperRef} variant="tertiary">
        <StickyScrollPageHeader
          title="🔒 Cognito Test Page"
          scrollContainerRef={scrollContainerRef}
          headerVariant="tertiary"
          onClickBack={() => {
            history.push("/home-v2/debug");
          }}
        />
        <Stack
          ref={scrollContainerRef}
          sx={(theme: Theme) => ({
            flexDirection: "column",
            padding: theme.spacing(5),
            gap: theme.spacing(4),
          })}
        >
          <EmailAuthTest
            cognitoAuth={cognitoAuth}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            setResult={setResult}
            setApiResult={setApiResult}
            currentUser={currentUser}
            setCurrentUser={setCurrentUser}
          />

          <PhoneAuthTest
            cognitoAuth={cognitoAuth}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            setResult={setResult}
            setApiResult={setApiResult}
            currentUser={currentUser}
            setCurrentUser={setCurrentUser}
          />

          <SessionApiTest
            cognitoAuth={cognitoAuth}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            setResult={setResult}
            setApiResult={setApiResult}
            currentUser={currentUser}
            setCurrentUser={setCurrentUser}
          />

          {result && (
            <Card>
              <CardContent>
                <Text
                  variant="h6"
                  sx={(theme: Theme) => ({
                    marginBottom: theme.spacing(2),
                  })}
                >
                  Result
                </Text>
                <Box
                  component="pre"
                  sx={(theme: Theme) => ({
                    backgroundColor: theme.palette.grey[100],
                    padding: theme.spacing(2),
                    borderRadius: theme.spacing(1),
                    fontFamily: "monospace",
                    fontSize: "12px",
                    overflow: "auto",
                    maxHeight: "200px",
                    margin: 0,
                  })}
                >
                  {result}
                </Box>
              </CardContent>
            </Card>
          )}

          {apiResult && (
            <Card>
              <CardContent>
                <Text
                  variant="h6"
                  sx={(theme: Theme) => ({
                    marginBottom: theme.spacing(2),
                  })}
                >
                  API Test Result
                </Text>
                <Box
                  component="pre"
                  sx={(theme: Theme) => ({
                    backgroundColor: theme.palette.grey[100],
                    padding: theme.spacing(2),
                    borderRadius: theme.spacing(1),
                    fontSize: "11px",
                    maxHeight: "200px",
                    overflow: "auto",
                    margin: 0,
                  })}
                >
                  {apiResult || "API test results will appear here..."}
                </Box>
              </CardContent>
            </Card>
          )}
        </Stack>
      </PageWrapper>
    </ThemeProvider>
  );
}
